import z from "zod";

import {
  InputMaybe,
  ListIntegrationsQuery,
  NgpVanDatabaseModeGqlType,
  NgpVanTagIntegrationInput,
} from "~frontend/__generated__/graphql";
import { NgpVanActionFieldSchema } from "~frontend/components/forms/NgpVanObjectAutocomplete/types";
import { TagDetail } from "~frontend/graphql/tags";

export type TagType = "flat" | "singleSelect";

export const IntegrationsSchema = z.array(NgpVanActionFieldSchema);

export type IntegrationsType = z.infer<typeof IntegrationsSchema>;

export const EditTagFormSchema = z.object({
  name: z.string().min(1, "Tag name is required"),
  integrations: IntegrationsSchema,
});

export type EditTagFormType = z.infer<typeof EditTagFormSchema>;

export const EditTagGroupFormSchema = z.object({
  name: z.string().min(1, "Tag name is required"),
  tags: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().min(1, "Option name is required"),
      integrations: IntegrationsSchema,
    })
  ),
});

export type EditTagGroupFormType = z.infer<typeof EditTagGroupFormSchema>;

export function integrationFormValuesToGqlInput(
  values: IntegrationsType
): InputMaybe<NgpVanTagIntegrationInput[]> {
  if (!values || values.length === 0) {
    return null;
  }

  return values.map(integration => ({
    integrationId: integration.integrationIdAndDbMode.split("|")[0],
    databaseMode: integration.integrationIdAndDbMode.split(
      "|"
    )[1] as NgpVanDatabaseModeGqlType,
    tagActionsJson: JSON.stringify([
      {
        actionType: integration.actionType,
        activistCodeId: integration.activistCodeId
          ? Number(integration.activistCodeId)
          : undefined,
        resultCodeId: integration.resultCodeId
          ? Number(integration.resultCodeId)
          : undefined,
        surveyQuestionId: integration.surveyQuestionId
          ? Number(integration.surveyQuestionId)
          : undefined,
        surveyResponseId: integration.surveyResponseId
          ? Number(integration.surveyResponseId)
          : undefined,
      },
    ]),
  }));
}

function actionForModeToFormValues(
  actionForMode: TagDetail["integrationActions"][number]["ngpVanMyCampaignActions"][number]
): Omit<IntegrationsType[number], "integrationIdAndDbMode"> {
  switch (actionForMode.__typename) {
    case "NgpVanTagIntegrationActionApplyActivistCode":
      return {
        actionType: "APPLY_ACTIVIST_CODE",
        activistCodeId: String(actionForMode.activistCodeId),
        resultCodeId: "",
        surveyQuestionId: "",
        surveyResponseId: "",
      };
    case "NgpVanTagIntegrationActionApplyResultCode":
      return {
        actionType: "APPLY_RESULT_CODE",
        activistCodeId: "",
        resultCodeId: String(actionForMode.resultCodeId),
        surveyQuestionId: "",
        surveyResponseId: "",
      };
    case "NgpVanTagIntegrationActionApplySurveyResponse":
      return {
        actionType: "APPLY_SURVEY_RESPONSE",
        activistCodeId: "",
        resultCodeId: "",
        surveyQuestionId: String(actionForMode.surveyQuestionId),
        surveyResponseId: String(actionForMode.surveyResponseId),
      };
  }
}

export function tagIntegrationsToFormValues(
  integrations: TagDetail["integrationActions"]
): IntegrationsType {
  return integrations.flatMap(action => {
    const integrations: IntegrationsType = [];

    action.ngpVanMyVotersActions.forEach(ngpVanMyVotersAction => {
      integrations.push({
        integrationIdAndDbMode: `${action.integration.id}|${NgpVanDatabaseModeGqlType.MyVoters}`,
        ...actionForModeToFormValues(ngpVanMyVotersAction),
      });
    });

    action.ngpVanMyCampaignActions.forEach(ngpVanMyCampaignAction => {
      integrations.push({
        integrationIdAndDbMode: `${action.integration.id}|${NgpVanDatabaseModeGqlType.MyCampaign}`,
        ...actionForModeToFormValues(ngpVanMyCampaignAction),
      });
    });

    return integrations;
  });
}

export function emptyIntegration(): IntegrationsType[number] {
  return {
    integrationIdAndDbMode: "",
    actionType: "",
    activistCodeId: "",
    resultCodeId: "",
    surveyQuestionId: "",
    surveyResponseId: "",
  };
}

export type NgpVanIntegration = Extract<
  ListIntegrationsQuery["listIntegrations"][number],
  {
    __typename?: "NgpVanIntegration";
  }
>;
